import router from '@/router'  // 确保引入 Vue Router 实例

const state = {
    // 用户访问过的页面（标签页）
    visitedViews: [],
    // 缓存的路由（keep-alive 使用）
    cachedViews: []
};

const mutations = {
    // 添加访问过的标签
    ADD_VISITED_VIEW: (state, view) => {
        if (state.visitedViews.some(v => v.path === view.path)) return;
        state.visitedViews.push({
            path: view.path,
            name: view.name,
            meta: view.meta ? { ...view.meta } : {},
            query: view.query || {},
            params: view.params || {},
            title: view.meta?.title || 'no-name'
        });
    },

    // 添加缓存标签
    ADD_CACHED_VIEW: (state, view) => {
        if (state.cachedViews.includes(view.meta.name)) return;
        if (view.meta && !view.meta.noCache) {
            state.cachedViews.push(view.meta.name);
        }
    },

    // 删除选中的标签
    DEL_VISITED_VIEW: (state, view) => {
        state.visitedViews = state.visitedViews.filter(v => v.path !== view.path);
    },

    // 删除缓存标签
    DEL_CACHED_VIEW: (state, view) => {
        state.cachedViews = state.cachedViews.filter(name => name !== view.meta.name);
    },

    // 删除其他访问过的标签
    DEL_OTHERS_VISITED_VIEWS: (state, view) => {
        state.visitedViews = state.visitedViews.filter(v => v.meta.affix || v.path === view.path);
    },

    // 删除其他缓存标签
    DEL_OTHERS_CACHED_VIEWS: (state, view) => {
        state.cachedViews = state.cachedViews.includes(view.meta.name) ? [view.meta.name] : [];
    },

    // 删除所有访问过的标签
    DEL_ALL_VISITED_VIEWS: (state) => {
        state.visitedViews = state.visitedViews.filter(tag => tag.meta.affix);
        router.push(state.visitedViews[0].path || '/dashboard'); // 跳转到第一个标签页
    },

    // 删除所有缓存标签
    DEL_ALL_CACHED_VIEWS: (state) => {
        state.cachedViews = [];
    },

    // 更新访问过的标签信息
    UPDATE_VISITED_VIEW: (state, view) => {
        state.visitedViews = state.visitedViews.map(v => {
            if (v.path === view.path) {
                return {
                    ...v,
                    title: view.meta?.title || v.title,
                    query: view.query || v.query,
                    params: view.params || v.params
                };
            }
            return v;
        });
    },

    // 删除右侧的标签
    DEL_RIGHT_VIEWS: (state, view) => {
        const index = state.visitedViews.findIndex(v => v.path === view.path);
        if (index !== -1) {
            // 保留 index 之前的所有标签，包括 affix 为 true 的标签
            const affixTagsBefore = state.visitedViews.slice(0, index + 1);

            // 获取从 index 开始的所有标签（右侧的标签）
            const rightTags = state.visitedViews.slice(index + 1);

            // 过滤出右侧的 affix == true 标签
            const affixTagsAfter = rightTags.filter(tag => tag.meta.affix);

            // 保留 affix 为 true 的标签，不做删除
            state.visitedViews = [...affixTagsBefore, ...affixTagsAfter];

            // 更新缓存视图，排除掉删除的视图
            state.cachedViews = state.cachedViews.filter(name =>
                state.visitedViews.some(v => v.meta.name === name)
            );
        }
        router.push(view.path || '/dashboard'); // 跳转到第一个标签页
    },

    // 删除左侧的标签
    DEL_LEFT_VIEWS: (state, view) => {
        const index = state.visitedViews.findIndex(v => v.path === view.path);
        if (index !== -1) {
            // 保留当前标签之前的所有标签（包括 affix 为 true 的标签）
            const affixTagsBefore = state.visitedViews.slice(0, index);

            // 获取当前标签之后的所有标签（右侧的标签）
            const leftTags = state.visitedViews.slice(0, index);

            // 过滤出左侧的 affix == true 标签
            const affixTagsBeforeOnly = leftTags.filter(tag => tag.meta.affix);

            // 合并保留的 affix == true 标签和剩下的标签
            state.visitedViews = [...affixTagsBeforeOnly, ...state.visitedViews.slice(index)];

            // 更新缓存视图，排除掉删除的视图
            state.cachedViews = state.cachedViews.filter(name =>
                state.visitedViews.some(v => v.meta.name === name)
            );
        }
        router.push(view.path || '/dashboard'); // 跳转到第一个标签页
    }

};

const actions = {
    // 新增当前标签页
    addView({ dispatch }, view) {
        dispatch('addVisitedView', view);
        dispatch('addCachedView', view);
    },

    // 新增访问过的标签
    addVisitedView({ commit }, view) {
        commit('ADD_VISITED_VIEW', view);
    },

    // 新增缓存标签
    addCachedView({ commit }, view) {
        commit('ADD_CACHED_VIEW', view);
    },

    // 删除当前标签页
    delView({ dispatch, state }, view) {
        return new Promise(async resolve => {
            const isCurrent = view.path === router.history.current.path; // 检查是否是当前路由
            await dispatch('delVisitedView', view);
            await dispatch('delCachedView', view);

            // 如果删除的是当前路由，自动跳转到第一个标签
            if (isCurrent && state.visitedViews.length > 0) {
                const firstView = state.visitedViews[0];
                router.push(firstView.path); // 跳转到第一个标签页
            }

            resolve({
                visitedViews: [...state.visitedViews],
                cachedViews: [...state.cachedViews]
            });
        });
    },

    // 删除访问过的标签
    delVisitedView({ commit, state }, view) {
        return new Promise(resolve => {
            commit('DEL_VISITED_VIEW', view);
            resolve([...state.visitedViews]);
        });
    },

    // 删除缓存标签
    delCachedView({ commit, state }, view) {
        return new Promise(resolve => {
            commit('DEL_CACHED_VIEW', view);
            resolve([...state.cachedViews]);
        });
    },

    // 删除其他标签
    delOthersViews({ dispatch, state }, view) {
        return new Promise(resolve => {
            dispatch('delOthersVisitedViews', view);
            dispatch('delOthersCachedViews', view);
            resolve({
                visitedViews: [...state.visitedViews],
                cachedViews: [...state.cachedViews]
            });
        });
    },

    // 删除其他访问过的标签
    delOthersVisitedViews({ commit, state }, view) {
        return new Promise(resolve => {
            commit('DEL_OTHERS_VISITED_VIEWS', view);
            resolve([...state.visitedViews]);
        });
    },

    // 删除其他缓存标签
    delOthersCachedViews({ commit, state }, view) {
        return new Promise(resolve => {
            commit('DEL_OTHERS_CACHED_VIEWS', view);
            resolve([...state.cachedViews]);
        });
    },

    // 删除所有标签
    delAllViews({ dispatch, state }) {
        return new Promise(resolve => {
            dispatch('delAllVisitedViews');
            dispatch('delAllCachedViews');
            resolve({
                visitedViews: [...state.visitedViews],
                cachedViews: [...state.cachedViews]
            });
        });
    },

    // 删除所有访问过的标签
    delAllVisitedViews({ commit, state }) {
        return new Promise(resolve => {
            commit('DEL_ALL_VISITED_VIEWS');
            resolve([...state.visitedViews]);
        });
    },

    // 删除所有缓存标签
    delAllCachedViews({ commit, state }) {
        return new Promise(resolve => {
            commit('DEL_ALL_CACHED_VIEWS');
            resolve([...state.cachedViews]);
        });
    },

    // 更新访问过的标签
    updateVisitedView({ commit }, view) {
        commit('UPDATE_VISITED_VIEW', view);
    },

    // 删除右侧标签
    delRightTags({ commit }, view) {
        return new Promise(resolve => {
            commit('DEL_RIGHT_VIEWS', view);
            resolve([...state.visitedViews]);
        });
    },

    // 删除左侧标签
    delLeftTags({ commit }, view) {
        return new Promise(resolve => {
            commit('DEL_LEFT_VIEWS', view);
            resolve([...state.visitedViews]);
        });
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions
};
